import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Users, FileCheck2, Award, HeartPulse, UsersRound,
  Building2, CalendarCheck, Star, Globe2, ArrowRight, Sparkles,
} from "lucide-react";

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-5 flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand font-outfit font-bold">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-4xl sm:text-5xl font-outfit font-bold text-white leading-tight tracking-tight">{value}</div>
      {sub && <div className="text-xs text-white/60 font-manrope">{sub}</div>}
    </div>
  );
}

export default function Trust() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/public/trust-stats").then((r) => setS(r.data)).catch(() => setS({}));
  }, []);

  const asOf = s?.generated_at ? new Date(s.generated_at).toLocaleString() : "";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-10">
      {/* Header */}
      <div className="grid md:grid-cols-3 gap-8 items-end">
        <div className="md:col-span-2">
          <div className="inline-flex items-center gap-2 bg-gold-50 text-gold-600 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Live platform trust snapshot
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-outfit font-bold text-white leading-[1.05]">
            Every number here is a verified security professional&nbsp;on&nbsp;SecuSource.
          </h1>
          <p className="text-white/70 mt-4 font-manrope text-base sm:text-lg max-w-2xl">
            No stock photos. No inflated claims. This page pulls live from the platform every time you refresh —
            share it with your own clients as proof you source from a vetted, cross-border workforce.
          </p>
        </div>
        <div className="hidden md:flex flex-col gap-2 items-end text-right">
          <div className="text-xs text-white/50 uppercase tracking-widest font-outfit font-bold">Snapshot</div>
          <div className="text-sm font-manrope text-white/60">{asOf || "loading…"}</div>
          <Button asChild className="mt-3 bg-brand hover:bg-brand-hover text-white">
            <Link to="/verify-badge" data-testid="trust-verify-cta">
              Verify a licence <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>

      {s ? (
        <>
          {/* Primary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={Users} label="Verified pros" value={s.total_verified_professionals ?? 0} sub="Approved & active" testId="trust-total-pros" />
            <Stat icon={Building2} label="Companies onboarded" value={s.approved_companies ?? 0} sub="Approved clients" testId="trust-companies" />
            <Stat icon={CalendarCheck} label="Completed bookings" value={s.completed_bookings ?? 0} sub="On-platform deployments" testId="trust-bookings" />
            <Stat icon={Star} label="Avg pro rating" value={s.average_professional_rating > 0 ? `${s.average_professional_rating}/5` : "—"} sub={`${s.rated_professionals ?? 0} rated pros`} testId="trust-rating" />
          </div>

          {/* Country + licence breakdown */}
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3 flex items-center gap-2"><Globe2 className="w-3.5 h-3.5" /> Cross-border coverage</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={Globe2} label="Ireland pros" value={s.ie_professionals ?? 0} sub="Available in IE" testId="trust-ie" />
              <Stat icon={Globe2} label="UK pros" value={s.uk_professionals ?? 0} sub="Available in UK" testId="trust-uk" />
              <Stat icon={FileCheck2} label="PSA on file" value={s.psa_licences_on_file ?? 0} sub="Irish licences" testId="trust-psa" />
              <Stat icon={FileCheck2} label="SIA on file" value={s.sia_licences_on_file ?? 0} sub="UK licences" testId="trust-sia" />
            </div>
          </div>

          {/* Specialist capability */}
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3 flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Specialist capability</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Stat icon={ShieldCheck} label="CP qualified" value={s.cp_qualified ?? 0} sub="Close protection" testId="trust-cp" />
              <Stat icon={HeartPulse} label="First aid certified" value={s.first_aid_certified ?? 0} sub="On-site medical support" testId="trust-firstaid" />
              <Stat icon={UsersRound} label="Active cluster teams" value={s.active_cluster_teams ?? 0} sub="Ready-made ops teams" testId="trust-teams" />
            </div>
          </div>

          {/* Footer CTA */}
          <div className="rounded-xl border border-white/10 bg-navy-900 text-white p-8 flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-xl">
              <div className="text-xs uppercase tracking-widest text-gold-300 font-outfit font-bold mb-2">Prove it to your own clients</div>
              <div className="font-outfit font-bold text-2xl leading-snug">Link this page from your quote emails. The numbers update themselves.</div>
              <p className="text-slate-300 font-manrope text-sm mt-2">
                URL: <code className="text-gold-300 bg-navy-800 rounded px-2 py-0.5">/trust</code> — public, always live.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="bg-[#0A0A0A] hover:bg-[#141414] text-white"><Link to="/employers" data-testid="trust-forcos-cta">For employers</Link></Button>
              <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10"><Link to="/register/company" data-testid="trust-register-cta">Register as a company</Link></Button>
            </div>
          </div>
        </>
      ) : (
        <div className="text-white/60 font-manrope">Loading live stats…</div>
      )}
    </div>
  );
}
